let stripeLoader;
let nextHandle = 1;
const checkouts = new Map();

function loadStripe() {
    if (typeof window.Stripe === 'function') return Promise.resolve(window.Stripe);
    if (!stripeLoader) {
        stripeLoader = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://js.stripe.com/endive/stripe.js';
            script.async = true;
            script.crossOrigin = 'anonymous';
            script.onload = () => typeof window.Stripe === 'function'
                ? resolve(window.Stripe) : reject(new Error('Stripe.js did not initialize'));
            script.onerror = () => { script.remove(); reject(new Error('Stripe.js could not load')); };
            document.head.appendChild(script);
        }).catch(error => { stripeLoader = null; throw error; });
    }
    return stripeLoader;
}

export function preloadStripeWalletCheckout() {
    return loadStripe().then(() => true);
}

function notify(state, event, value = '') {
    if (!state.disposed) state.onEvent(event, value);
}

function hasDisplayedPrice(state) {
    // Compose acknowledges the revision after applying the quoted price and renewal terms.
    return !state.localizedPricing || (state.priceRevision > 0 && state.priceRevision === state.acknowledgedRevision);
}

function updateWalletVisibility(state) {
    const shown = state.shown && !state.disposed;
    const enabled = state.enabled && hasDisplayedPrice(state);
    const appearance = shown ? (enabled ? 'enabled' : 'disabled') : 'hidden';
    if (state.appearance === appearance) return;
    state.appearance = appearance;
    state.container.style.pointerEvents = shown && enabled ? 'auto' : 'none';
    state.container.style.opacity = shown ? (enabled ? '1' : '0.5') : '0';
    state.container.inert = !shown || !enabled;
    state.container.setAttribute('aria-hidden', shown ? 'false' : 'true');
}

function positionWallet(state) {
    state.frame = null;
    if (state.disposed || !state.anchor || !state.visible) return;
    const rect = state.anchor.getBoundingClientRect();
    state.shown = state.anchor.isConnected && rect.width > 0 && rect.height > 1;
    const position = `${rect.left}:${rect.top}:${rect.width}:${rect.height}`;
    if (state.shown && state.position !== position) {
        state.position = position;
        const style = state.container.style;
        style.left = `${rect.left}px`;
        style.top = `${rect.top}px`;
        style.width = `${rect.width}px`;
        style.height = `${rect.height}px`;
    }
    updateWalletVisibility(state);
    // Compose can move the slot while scrolling without a DOM scroll or resize event.
    state.frame = window.requestAnimationFrame(() => positionWallet(state));
}

function detachWallet(state) {
    if (state.frame != null) window.cancelAnimationFrame(state.frame);
    state.frame = null;
    state.anchor = null;
    state.visible = false;
    state.shown = false;
    updateWalletVisibility(state);
}

function providerFailure(state, stage, error, unavailable = false) {
    if (state.disposed) return;
    // Stripe errors may contain payment details; retain only provider diagnostic identifiers.
    const identifier = value => typeof value === 'string' && /^[a-zA-Z0-9_]+$/.test(value) ? value : 'unknown';
    const type = identifier(error?.type || error?.name);
    const code = identifier(error?.code);
    // Checkout actions use paymentFailed.declineCode; older Stripe errors use decline_code.
    const declineCode = identifier(error?.paymentFailed?.declineCode || error?.decline_code || error?.declineCode);
    if (unavailable) {
        state.enabled = false;
        updateWalletVisibility(state);
        if (state.currencyPortal) {
            state.currencyPortal.enabled = false;
            updateWalletVisibility(state.currencyPortal);
        }
        notify(state, 'error', `${stage}:${type}:${code}:${declineCode}`);
    }
    console.warn(`Stripe wallet failed: stage=${stage}, kind=${state.kind}, type=${type}, code=${code}, declineCode=${declineCode}`);
    return {type, code, declineCode};
}

function validateSessionTotal(session) {
    // Validate Stripe's current amount; prices and trial terms are displayed with the offers.
    const amount = session.total?.total?.amount;
    if (typeof amount !== 'string' || !amount) throw new Error('Stripe checkout total is missing');
}

function publishSession(state, session) {
    validateSessionTotal(session);
    if (!state.localizedPricing) return;
    const item = session.lineItems?.[0];
    const divisor = session.minorUnitsAmountDivisor;
    if (session.lineItems?.length !== 1 || item.quantity !== 1 ||
        !/^[a-z]{3}$/i.test(session.currency) || !Number.isFinite(divisor) || divisor <= 0) {
        throw new Error('Invalid localized checkout price');
    }
    const unit = state.kind === 'subscription' ? item.unitAmount : session.total.total;
    const payment = session.total.total;
    for (const price of [unit, payment]) {
        if (typeof price?.amount !== 'string' || !price.amount ||
            !Number.isSafeInteger(price.minorUnitsAmount) || price.minorUnitsAmount < 0) {
            throw new Error('Invalid localized checkout amount');
        }
    }
    const quote = {
        currency: session.currency.toUpperCase(),
        formattedPrice: unit.amount, value: unit.minorUnitsAmount / divisor,
        paymentFormattedPrice: payment.amount, paymentValue: payment.minorUnitsAmount / divisor,
    };
    const fingerprint = JSON.stringify(quote);
    if (state.priceFingerprint !== fingerprint) {
        state.priceFingerprint = fingerprint;
        state.priceRevision = (state.priceRevision || 0) + 1;
        notify(state, 'price', JSON.stringify({...quote, revision: state.priceRevision}));
    }
    state.hasCurrencyOptions = (session.currencyOptions?.length || 0) > 0;
    reportCurrencyHeight(state);
    updateWalletVisibility(state);
    publishAvailability(state);
}

function publishAvailability(state) {
    // canConfirm also reflects missing details that the wallet collects during authorization.
    if (state.methodAvailable == null) return;
    if (state.publishedAvailability === state.methodAvailable) return;
    state.publishedAvailability = state.methodAvailable;
    notify(state, 'available', state.methodAvailable ? 'true' : 'false');
}

function reportCurrencyHeight(state) {
    if (!state.currencyHost || state.disposed) return;
    const height = state.hasCurrencyOptions ? Math.ceil(state.currencyHost.getBoundingClientRect().height) : 0;
    if (state.currencyHeight !== height) {
        state.currencyHeight = height;
        notify(state, 'currency_height', String(height));
    }
}

function finish(state, result) {
    state.pending?.resolve(result);
    state.pending = null;
    state.confirmEvent = null;
    if (state.currencyPortal) {
        state.currencyPortal.enabled = state.enabled;
        updateWalletVisibility(state.currencyPortal);
    }
}

async function submit(state) {
    if (!state.pending || !state.confirmEvent || state.submitting || state.disposed) return;
    state.submitting = true;
    try {
        const session = state.actions.getSession();
        publishSession(state, session);
        if (!hasDisplayedPrice(state) ||
            (state.localizedPricing && state.pending.priceRevision !== state.priceRevision)) {
            state.confirmEvent?.paymentFailed?.({reason: 'fail'});
            finish(state, 'cancelled');
            return;
        }
        const result = await state.actions.confirm({
            expressCheckoutConfirmEvent: state.confirmEvent,
            redirect: 'if_required',
        });
        if (state.disposed) return;
        if (result?.type === 'error') {
            state.confirmEvent?.paymentFailed?.({reason: 'fail'});
            const failure = providerFailure(state, 'confirmation', result.error);
            finish(state, JSON.stringify(failure));
        } else {
            state.completed = true;
            finish(state, 'submitted');
        }
    } catch (error) {
        state.confirmEvent?.paymentFailed?.({reason: 'fail'});
        // A transport failure can happen after Stripe accepts payment. Reconcile server-side.
        providerFailure(state, 'confirmation', error);
        finish(state, 'pending');
    } finally {
        state.submitting = false;
    }
}

export function mountStripeWalletCheckout(container, clientSecret, publishableKey, subscription, dark,
    buttonHeight, buttonCornerRadius, onEvent, currencyContainer, localizedPricing = false) {
    const id = nextHandle++;
    const state = {
        container, onEvent, disposed: false, enabled: true, shown: false, visible: false,
        kind: subscription ? 'subscription' : 'top_up',
        localizedPricing,
    };
    if (localizedPricing) {
        state.currencyPortal = {container: currencyContainer, enabled: true, shown: false, disposed: false};
        updateWalletVisibility(state.currencyPortal);
    }
    checkouts.set(id, state);
    updateWalletVisibility(state);
    const initialize = async () => {
        if (!/^pk_(live|test)_/.test(publishableKey) || !/^cs_(live|test)_[A-Za-z0-9]+_secret_/.test(clientSecret)) {
            throw new Error('Invalid wallet checkout credentials');
        }
        const Stripe = await loadStripe();
        if (state.disposed) return;
        state.checkout = Stripe(publishableKey).initCheckoutElementsSdk({
            clientSecret,
            adaptivePricing: {allowed: localizedPricing},
            elementsOptions: {
                appearance: {
                    theme: dark ? 'night' : 'stripe',
                    variables: {borderRadius: `${buttonCornerRadius}px`},
                },
            },
        });
        const loaded = await state.checkout.loadActions();
        if (state.disposed) return;
        if (loaded.type !== 'success') {
            providerFailure(state, 'initialization', loaded.error, true);
            return;
        }
        state.actions = loaded.actions;
        publishSession(state, state.actions.getSession());
        state.checkout.on('change', session => {
            if (!state.disposed) {
                try { publishSession(state, session); }
                catch (error) { providerFailure(state, 'session_change', error, true); }
            }
        });
        if (localizedPricing) {
            state.currencyHost = document.createElement('div');
            currencyContainer.appendChild(state.currencyHost);
            state.currencySelector = state.checkout.createCurrencySelectorElement();
            state.currencySelector.on('loaderror', event => providerFailure(state, 'currency_selector', event.error, true));
            state.currencySelector.on('ready', () => reportCurrencyHeight(state));
            state.currencyObserver = new ResizeObserver(() => reportCurrencyHeight(state));
            state.currencyObserver.observe(state.currencyHost);
            state.currencySelector.mount(state.currencyHost);
        }
        const element = state.checkout.createExpressCheckoutElement({
            buttonHeight,
            buttonType: {applePay: subscription ? 'subscribe' : 'buy', googlePay: subscription ? 'subscribe' : 'pay'},
            buttonTheme: {applePay: dark ? 'white' : 'black', googlePay: dark ? 'white' : 'black'},
            layout: {maxColumns: 1, maxRows: 1},
            paymentMethods: {applePay: 'auto', googlePay: 'auto', link: 'never', paypal: 'never', amazonPay: 'never', klarna: 'never'},
        });
        state.element = element;
        element.on('availablepaymentmethodschange', ({paymentMethods}) => {
            state.methodAvailable = Boolean(paymentMethods?.applePay?.available || paymentMethods?.googlePay?.available);
            publishAvailability(state);
        });
        element.on('loaderror', event => providerFailure(state, 'load', event.error, true));
        element.on('confirm', event => {
            if (state.disposed || !state.enabled || state.pending || state.completed || !hasDisplayedPrice(state)) {
                event.paymentFailed({reason: 'fail'});
                return;
            }
            state.confirmEvent = event;
            const promise = new Promise(resolve => { state.pending = {resolve, priceRevision: state.priceRevision}; });
            state.pending.promise = promise;
            if (state.currencyPortal) {
                state.currencyPortal.enabled = false;
                updateWalletVisibility(state.currencyPortal);
            }
            // Checkout opens the wallet itself. Validate the current account and offer
            // in the app before submitting this authorization to Stripe.
            notify(state, 'confirm', event.expressPaymentType);
        });
        element.on('cancel', () => {
            if (!state.submitting) finish(state, 'cancelled');
            notify(state, 'cancel');
        });
        element.mount(container);
    };
    void initialize().catch(error => providerFailure(state, 'initialization', error, true));
    return id;
}

export function confirmStripeWalletCheckout(id) {
    const state = checkouts.get(id);
    if (!state || state.disposed || !state.pending) return Promise.resolve('cancelled');
    const promise = state.pending.promise;
    void submit(state);
    return promise;
}

export function setStripeWalletCheckoutEnabled(id, enabled) {
    const state = checkouts.get(id);
    if (!state) return;
    state.enabled = enabled;
    updateWalletVisibility(state);
    if (state.currencyPortal) {
        state.currencyPortal.enabled = enabled && !state.pending && !state.submitting;
        updateWalletVisibility(state.currencyPortal);
    }
}

export function acknowledgeStripeWalletPrice(id, revision) {
    const state = checkouts.get(id);
    if (!state || state.disposed) return;
    state.acknowledgedRevision = revision;
    updateWalletVisibility(state);
}

export function attachStripeWalletCurrencySelector(id, anchor, visible) {
    const portal = checkouts.get(id)?.currencyPortal;
    if (!portal || portal.disposed) return;
    portal.anchor = anchor;
    portal.visible = visible;
    if (!visible) detachWallet(portal);
    else if (portal.frame == null) positionWallet(portal);
}

export function attachStripeWalletCheckout(id, anchor, visible) {
    const state = checkouts.get(id);
    if (!state || state.disposed) return;
    state.anchor = anchor;
    state.visible = visible;
    if (!visible) {
        detachWallet(state);
    } else if (state.frame == null) {
        positionWallet(state);
    }
}

export function detachStripeWalletCheckout(id) {
    const state = checkouts.get(id);
    if (state) {
        detachWallet(state);
        if (state.currencyPortal) detachWallet(state.currencyPortal);
    }
}

export function releaseStripeWalletCheckout(id) {
    const state = checkouts.get(id);
    if (!state) return;
    notify(state, 'available', 'false');
    state.disposed = true;
    detachWallet(state);
    if (!state.submitting) {
        state.confirmEvent?.paymentFailed?.({reason: 'fail'});
        finish(state, 'cancelled');
    } else {
        // The provider may finish after this popup closes; retain server reconciliation.
        finish(state, 'pending');
    }
    state.element?.destroy();
    if (state.currencyPortal) {
        state.currencyPortal.disposed = true;
        detachWallet(state.currencyPortal);
    }
    state.currencyObserver?.disconnect();
    state.currencySelector?.destroy();
    checkouts.delete(id);
}
